import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProjectOverviewTab } from "./ProjectOverviewTab";
import { ProjectCostTab } from "./ProjectCostTab";
import { ProjectFeedbackTab } from "./ProjectFeedbackTab";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ProjectDetailsTabsProps {
  project: any;
  isEditingInline: boolean;
  onEditingChange: (editing: boolean) => void;
}

export function ProjectDetailsTabs({ project, isEditingInline, onEditingChange }: ProjectDetailsTabsProps) {
  // Fetch clients for dropdown
  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch proposals for the selected client
  const { data: proposals } = useQuery({
    queryKey: ["proposals", project.client_id, project.proposal_id],
    queryFn: async () => {
      if (!project.client_id) return [];
      
      const { data, error } = await supabase
        .from("proposals")
        .select("id, title, client_id, status, value, final_value, tools")
        .eq("client_id", project.client_id)
        .order("title");
      
      if (error) throw error;
      
      // If project has a linked proposal that's not in the results, fetch it separately
      if (project.proposal_id && !data?.find(p => p.id === project.proposal_id)) {
        const { data: linkedProposal, error: linkedError } = await supabase
          .from("proposals")
          .select("id, title, client_id, status, value, final_value, tools")
          .eq("id", project.proposal_id)
          .single();
        
        if (!linkedError && linkedProposal) {
          return [...(data || []), linkedProposal].sort((a, b) => a.title.localeCompare(b.title));
        }
      }
      
      return data || [];
    },
    enabled: !!project.client_id,
  });

  return (
    <div className="flex-1">
      <Tabs defaultValue="overview" className="h-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="cost">Tools & Costs</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
        </TabsList>
        
        <div className="mt-6 h-full overflow-y-auto">
          <TabsContent value="overview" className="mt-0">
            <ProjectOverviewTab 
              project={project} 
              clients={clients || []}
              proposals={proposals || []}
              isEditingInline={isEditingInline}
              onEditingChange={onEditingChange}
            />
          </TabsContent>
          
          <TabsContent value="cost" className="mt-0">
            <ProjectCostTab 
              project={project} 
              isEditingInline={isEditingInline}
              onEditingChange={onEditingChange}
            />
          </TabsContent>
          
          <TabsContent value="feedback" className="mt-0">
            <ProjectFeedbackTab 
              project={project} 
              isEditingInline={isEditingInline}
              onEditingChange={onEditingChange}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}